import ModalComponent from '../../../../components/ModalComponent';
import { Controller, useForm } from 'react-hook-form';
import zod from 'zod';
import { AlarmCentralEditionFormSchema } from '../../../../services/AlarmCentralService/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import TextFieldComponent from '../../../../components/TextFieldComponent';
import { Grid2 } from '@mui/material';
import { useGetAllWebhookIntegrationByLicenseAsync } from '../../../../services/IntegrationService/WebhookService/hooks/useMutation';
import { useContext, useEffect, useState } from 'react';
import { IWebhookSimpleProps } from '../../../../services/IntegrationService/WebhookService/types';
import DropdownComponent from '../../../../components/DropdownComponent';
import {
  getAlarmCentralCompanyOptions,
  getAlarmCentralModelOptions,
  IAlarmCentralModelOption,
} from '../../../../services/AlarmCentralService/AlarmCentralUtils';
import ButtonComponent from '../../../../components/ButtonComponent';
import {
  useCreateAlarmCentralAsync,
  useUpdateAlarmCentralByIdAsync,
} from '../../../../services/AlarmCentralService/hooks/useMutations';
import { AppContext } from '../../../../context/AppContextProvider';
import CheckboxComponent from 'components/CheckboxComponent';
import {
  useCreateVirtualAlarmCentral,
  useUpdateVirtualAlarmCentral,
} from 'services/AlarmCentralService/Virtual/hooks/useMutation';
import { useTranslation } from 'react-i18next';

type AlarmCentralEditionFormInput = zod.infer<
  typeof AlarmCentralEditionFormSchema
>;

interface AlarmCentralEditionModalProps {
  isOpen: boolean;
  handleClose: () => void;
  selectedItem: any;
  setIsOpen: (isOpen: boolean) => void;
}

const AlarmCentralEditionModal = ({
  isOpen,
  handleClose,
  selectedItem,
  setIsOpen,
}: AlarmCentralEditionModalProps) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors },
  } = useForm<AlarmCentralEditionFormInput>({
    resolver: zodResolver(AlarmCentralEditionFormSchema),
    defaultValues: {
      name: '',
      password: '',
      macAddress: '',
      isPartitionable: false,
      account: '',
      isVirtual: false,
      ip: '',
      port: '',
    },
  });
  const { showSnackbar } = useContext(AppContext);
  const [webhookOptions, setWebhookOptions] = useState<IWebhookSimpleProps[]>(
    [],
  );
  const [key, setKey] = useState(0);
  const [modelOptions, setModelOptions] =
    useState<IAlarmCentralModelOption[]>();
  const { mutateAsync: mutateGetAllWebhookIntegrationByLicenseAsync } =
    useGetAllWebhookIntegrationByLicenseAsync();
  const {
    mutate: mutateCreateAlarmCentralAsync,
    isLoading: isLoadingCreateAlarmCentralAsync,
  } = useCreateAlarmCentralAsync();
  const {
    mutate: mutateUpdateAlarmCentralByIdAsync,
    isLoading: isLoadingUpdateAlarmCentralByIdAsync,
  } = useUpdateAlarmCentralByIdAsync();
  const {
    mutate: mutateCreateVirtualAlarmCentralAsync,
    isLoading: isLoadingCreateVirtualAlarmCentralAsync,
  } = useCreateVirtualAlarmCentral();
  const {
    mutate: mutateUpdateVirtualAlarmCentralByIdAsync,
    isLoading: isLoadingUpdateVirtualAlarmCentralByIdAsync,
  } = useUpdateVirtualAlarmCentral();

  const onClose = () => {
    setIsOpen(false);
    reset();
    handleClose();
  };

  const centralTypeOptions = [
    { label: t('alarmCentral.physical'), value: 'PHYSICAL' },
    { label: t('alarmCentral.virtual'), value: 'VIRTUAL' },
  ];

  const handleSave = (data: AlarmCentralEditionFormInput) => {
    if (getValues('isVirtual')) {
      data.company = 'ALERTAI';
      createOrUpdateVirtualAlarmCentral(data);
      return;
    }
    if (data.id) {
      mutateUpdateAlarmCentralByIdAsync(
        { data, id: data.id },
        {
          onSuccess: () => {
            showSnackbar('Central atualizada com sucesso', 'success');
            onClose();
          },
        },
      );
      return;
    }

    mutateCreateAlarmCentralAsync(data, {
      onSuccess: () => {
        showSnackbar('Central criada com sucesso', 'success');
        onClose();
      },
    });
  };

  const createOrUpdateVirtualAlarmCentral = (
    data: AlarmCentralEditionFormInput,
  ) => {
    if (data.id) {
      mutateUpdateVirtualAlarmCentralByIdAsync(
        { alarmCentral: data, id: data.id },
        {
          onSuccess: () => {
            showSnackbar('Central virtual atualizada com sucesso', 'success');
            onClose();
          },
        },
      );
      return;
    }

    mutateCreateVirtualAlarmCentralAsync(data, {
      onSuccess: () => {
        showSnackbar('Central virtual criada com sucesso', 'success');
        onClose();
      },
    });
  };

  useEffect(() => {
    mutateGetAllWebhookIntegrationByLicenseAsync().then(setWebhookOptions);
  }, []);

  const selectedCompany = watch('company');
  watch('isVirtual');

  useEffect(() => {
    if (selectedCompany) {
      const models = getAlarmCentralModelOptions.find(
        (m) => m.parentValue === selectedCompany,
      );
      if (models) {
        setModelOptions(models.children);
      }
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedItem) {
      reset(selectedItem, { keepDefaultValues: true });
      setKey(Math.random() * 20);
    } else reset();
  }, [selectedItem]);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t('alarmCentral.editionTitle')}
    >
      <Grid2 container columnSpacing={2} key={key}>
        <Grid2 size={{ md: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextFieldComponent
                onChangeText={onChange}
                value={value}
                label={t('common.name')}
                errorMessage={error?.message}
              />
            )}
            name={'name'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ md: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DropdownComponent
                value={value ? 'VIRTUAL' : 'PHYSICAL'}
                options={centralTypeOptions}
                optionLabel={'label'}
                optionValue={'value'}
                onChange={(val) =>
                  val === 'VIRTUAL' ? onChange(true) : onChange(false)
                }
                label={t('alarmCentral.type')}
              />
            )}
            name={'isVirtual'}
            control={control}
          />
        </Grid2>
        {!getValues('isVirtual') ? (
          <>
            <Grid2 size={{ md: 6, xs: 12 }}>
              <Controller
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextFieldComponent
                    mask={'**:**:**:**:**:**'}
                    onChangeText={onChange}
                    value={value?.toUpperCase()}
                    errorMessage={error?.message}
                    label={t('alarmCentral.macAddress')}
                  />
                )}
                name="macAddress"
                control={control}
              />
            </Grid2>
            {selectedCompany == 'PARADOX' && (
              <>
                <Grid2 size={{ md: 6, xs: 12 }}>
                  <Controller
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextFieldComponent
                        onChangeText={onChange}
                        value={value}
                        errorMessage={error?.message}
                        label={'IP'}
                      />
                    )}
                    name="ip"
                    control={control}
                  />
                </Grid2>
                <Grid2 size={{ md: 6, xs: 12 }}>
                  <Controller
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <TextFieldComponent
                        onChangeText={onChange}
                        value={value}
                        errorMessage={error?.message}
                        label={'Porta'}
                      />
                    )}
                    name="port"
                    control={control}
                  />
                </Grid2>
              </>
            )}
            <Grid2 size={{ md: 6, xs: 12 }}>
              <Controller
                render={({ field: { onChange, value } }) => (
                  <DropdownComponent
                    label={t('common.brand')}
                    value={value}
                    optionLabel={'label'}
                    optionValue={'value'}
                    options={getAlarmCentralCompanyOptions}
                    onChange={onChange}
                  />
                )}
                name="company"
                control={control}
              />
            </Grid2>
            <Grid2 size={{ md: 6, xs: 12 }}>
              <Controller
                render={({ field: { onChange, value } }) => (
                  <DropdownComponent
                    label={t('common.model')}
                    value={value}
                    disabled={!selectedCompany}
                    optionLabel={'label'}
                    optionValue={'value'}
                    options={modelOptions ?? []}
                    onChange={onChange}
                  />
                )}
                name="model"
                control={control}
              />
            </Grid2>
            <Grid2 size={{ md: 6, xs: 12 }}>
              <Controller
                render={({ field: { onChange, value } }) => (
                  <TextFieldComponent
                    onChangeText={onChange}
                    value={value}
                    label={t('alarmCentral.password')}
                  />
                )}
                name="password"
                control={control}
              />
            </Grid2>
          </>
        ) : (
          <>
            <Grid2 size={{ md: 6, xs: 12 }}>
              <Controller
                render={({ field: { onChange, value } }) => (
                  <TextFieldComponent
                    onChangeText={onChange}
                    value={value}
                    label={t('alarmCentral.account')}
                  />
                )}
                name="account"
                control={control}
              />
            </Grid2>
            <Grid2 size={{ md: 6, xs: 12 }}>
              <Controller
                render={({ field: { onChange, value } }) => (
                  <CheckboxComponent
                    value={value}
                    label={t('alarmCentral.partitionable')}
                    onChange={onChange}
                  />
                )}
                name="isPartitionable"
                control={control}
              />
            </Grid2>
          </>
        )}
        <Grid2 size={12}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingCreateAlarmCentralAsync ||
              isLoadingUpdateAlarmCentralByIdAsync ||
              isLoadingCreateVirtualAlarmCentralAsync ||
              isLoadingUpdateVirtualAlarmCentralByIdAsync
            }
          >
            {t('common.save')}
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </ModalComponent>
  );
};

export default AlarmCentralEditionModal;
